import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import { MD_BREAKPOINT } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

interface Args {
	currentPage: number;
	pageSize: number;
	totalItems: number;
	changePage: (newPage: number) => void;
}

export default class AccountLogsPageNavigationComponent extends Component<Args> {
	@tracked numberOfPagesToShow = 7;

	get totalPages(): number {
		return Math.ceil(this.args.totalItems / this.args.pageSize);
	}

	@cached
	get pageNumbers(): (number | string)[] {
		if (this.numberOfPagesToShow >= this.totalPages) {
			return Array.from({ length: this.totalPages }, (_, i) => i + 1);
		}

		const pages = [],
			{ currentPage } = this.args;

		// Always include the first page
		pages.push(1);

		// Determine the start and end page based on the current page
		let startPage, endPage;

		const middlePoint = Math.ceil(this.numberOfPagesToShow / 2),
			sidePageCount = Math.floor((this.numberOfPagesToShow - 5) / 2); // 5 accounts for first, last, selected page, and two ellipses

		if (currentPage <= middlePoint) {
			startPage = 2;
			endPage = this.numberOfPagesToShow - 2;
		} else if (currentPage >= this.totalPages - middlePoint + 1) {
			startPage = this.totalPages - this.numberOfPagesToShow + 3;
			endPage = this.totalPages - 1;
		} else {
			startPage = currentPage - sidePageCount;
			endPage = currentPage + sidePageCount;
		}

		// Add start ellipsis if needed
		if (startPage > 2) {
			pages.push('...');
		}

		// Add page numbers
		for (let i = startPage; i <= endPage; i++) {
			pages.push(i);
		}

		// Add end ellipsis if needed
		if (endPage < this.totalPages - 1) {
			pages.push('...');
		}

		// Always include the last page
		pages.push(this.totalPages);

		return pages;
	}

	get isPreviousDisabled(): boolean {
		return this.args.currentPage === 1;
	}

	get isNextDisabled(): boolean {
		return this.args.currentPage === this.totalPages;
	}

	get isShown() {
		return !!this.pageNumbers.length;
	}

	@action
	changePage(value: string | number) {
		const newPage = value as number;
		if (newPage !== this.args.currentPage && newPage > 0 && newPage <= this.totalPages) {
			this.args.changePage(newPage);
		}
	}

	@action
	openPreviousPage() {
		this.changePage(this.args.currentPage - 1);
	}

	@action
	openNextPage() {
		this.changePage(this.args.currentPage + 1);
	}

	@action
	setNumberOfAdditionalPagesShown() {
		this.numberOfPagesToShow = window.innerWidth < MD_BREAKPOINT ? 7 : 9;
	}
	<template>
		{{#if this.isShown}}
			<div
				class="page-navigation"
				{{onWindowEvents this.setNumberOfAdditionalPagesShown "resize" 200}}
			>
				<a
					role="button"
					class="page-navigation__button {{if this.isPreviousDisabled 'disabled'}}"
					{{on "click" this.openPreviousPage}}
				>
					<CommonUiIconComponent @name="ChevronLeft" @size="sm" />
				</a>
				<div class="page-navigation__numbers">
					{{#each this.pageNumbers as |pageNumber|}}
						{{#if (eq pageNumber "...")}}
							<a
								role="button"
								class="page-navigation__number page-navigation__number--ellipsis"
							>
								{{pageNumber}}
							</a>
						{{else}}
							<a
								role="button"
								class="page-navigation__number
									{{if
										(eq pageNumber @currentPage)
										'page-navigation__number--selected'
									}}"
								{{on "click" (fn this.changePage pageNumber)}}
							>
								{{pageNumber}}
							</a>
						{{/if}}
					{{/each}}
				</div>
				<a
					role="button"
					class="page-navigation__button {{if this.isNextDisabled 'disabled'}}"
					{{on "click" this.openNextPage}}
				>
					<CommonUiIconComponent @name="ChevronRight" @size="sm" />
				</a>
			</div>
		{{/if}}
	</template>
}
